'use client';

import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import React from 'react';
import { useForm } from 'react-hook-form';

import { useTranslation } from '@/lib/i18n';

import Button from './Button';
import Input from './Input';

const UserLogin: React.FC = () => {
  const { t } = useTranslation();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (values: any) => {
    try {
      const result = await signIn('credentials', {
        email: values.email,
        password: values.password,
        redirect: false,
      });

      if (result?.error) {
        setError('root', { message: t('auth.invalidCredentials') || 'Credenciales inválidas' });
      } else {
        router.push('/');
      }
    } catch (error) {
      setError('root', { message: t('auth.loginError') || 'Error al iniciar sesión' });
    }
  };

  return (
    <div className="flex flex-col items-center justify-center max-w-sm mx-auto">
      <div className="flex flex-row items-center justify-center py-5">
        <h1 className="font-bold text-3xl">{t('auth.loginTitle')}</h1>
      </div>
      <div className="py-5 w-full">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label={t('auth.email')}
            type="email"
            placeholder="email@mail.com"
            data-testid="login-email"
            {...register('email', {
              required: t('auth.emailRequired') || 'El email es requerido',
              pattern: {
                value: /^\S+@\S+$/i,
                message: t('auth.invalidEmail') || 'Email inválido',
              },
            })}
            error={errors.email?.message}
            fullWidth
          />

          <Input
            label={t('auth.password')}
            type="password"
            placeholder="****"
            data-testid="login-password"
            {...register('password', {
              required: t('auth.passwordRequired') || 'La contraseña es requerida',
            })}
            error={errors.password?.message}
            fullWidth
          />

          {errors.root && <div className="text-sm text-red-500 text-center">{errors.root.message}</div>}

          <div className="flex flex-col items-center gap-4 pt-4">
            <Button type="submit" variant="primary" disabled={isSubmitting} data-testid="login-submit">
              {isSubmitting ? t('auth.loggingIn') || 'Iniciando sesión...' : t('auth.login')}
            </Button>

            <div>
              <a href={'/forgot-password'} className="text-sm text-primary hover:text-primary-hover underline">
                {t('auth.forgotPasswordLink')}
              </a>
            </div>

            <div className="flex flex-row gap-2 text-sm">
              <span>{t('auth.noAccount')}</span>
              <a href={'/register'} className="text-primary hover:text-primary-hover underline">
                {t('auth.register')}
              </a>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserLogin;
